import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { CheckCircle, XCircle, AlertCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { apiService } from '../services/api';

// --- Failure diagnostics: what we sent + what came back (populated only on a
// failed check). Lets us see the exact ping and the response headers/body — e.g.
// a Cloudflare 429/503 with its cf-ray / retry-after / challenge page.
function parseJsonObj(s) {
  if (!s) return null;
  try {
    const o = JSON.parse(s);
    return o && typeof o === 'object' ? o : null;
  } catch {
    return null;
  }
}

function buildCurl(url, reqHeaders) {
  let cmd = `curl -i '${url}'`;
  if (reqHeaders) {
    for (const [k, v] of Object.entries(reqHeaders)) cmd += ` \\\n  -H '${k}: ${v}'`;
  }
  return cmd;
}

function KVList({ obj }) {
  if (!obj || !Object.keys(obj).length) return <div className="text-xs text-gray-400">(none)</div>;
  return (
    <div className="overflow-x-auto">
      <table className="text-xs font-mono">
        <tbody>
          {Object.entries(obj).map(([k, v]) => (
            <tr key={k}>
              <td className="pr-3 py-0.5 text-gray-500 align-top whitespace-nowrap">{k}</td>
              <td className="py-0.5 text-gray-800 break-all">{String(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FailureDiagnostics({ check }) {
  const reqHeaders = parseJsonObj(check.request_headers);
  const respHeaders = parseJsonObj(check.response_headers);
  const body = check.response_body;
  if (!reqHeaders && !respHeaders && !body) return null;
  return (
    <details className="mt-3">
      <summary className="cursor-pointer text-xs font-medium text-error-700 select-none">
        Diagnostics (request &amp; response)
      </summary>
      <div className="mt-2 space-y-3">
        <div>
          <div className="text-xs font-semibold text-gray-600 mb-1">Request (command)</div>
          <pre className="text-xs bg-gray-100 rounded-md p-2 overflow-x-auto whitespace-pre-wrap break-all">{buildCurl(check.url, reqHeaders)}</pre>
        </div>
        {reqHeaders && (
          <div>
            <div className="text-xs font-semibold text-gray-600 mb-1">Request headers</div>
            <KVList obj={reqHeaders} />
          </div>
        )}
        {respHeaders && (
          <div>
            <div className="text-xs font-semibold text-gray-600 mb-1">Response headers</div>
            <KVList obj={respHeaders} />
          </div>
        )}
        {body && (
          <div>
            <div className="text-xs font-semibold text-gray-600 mb-1">Response body</div>
            <pre className="text-xs bg-slate-900 text-slate-100 rounded-md p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-64">{body}</pre>
          </div>
        )}
      </div>
    </details>
  );
}

const HealthChecks = () => {
  const [healthChecks, setHealthChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [triggering, setTriggering] = useState(false);

  useEffect(() => {
    fetchHealthChecks();
    const interval = setInterval(fetchHealthChecks, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const fetchHealthChecks = async () => {
    try {
      const response = await apiService.getHealthChecks();
      setHealthChecks(response.data);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const triggerHealthCheck = async () => {
    setTriggering(true);

    // Snapshot the most recent timestamp so we can detect when the worker writes new rows
    const beforeTs = healthChecks.length > 0
      ? new Date(healthChecks[0].timestamp).getTime()
      : 0;

    try {
      await apiService.triggerHealthCheck();
    } catch (err) {
      alert('Failed to trigger health check: ' + (err.response?.data?.detail || err.message));
      setTriggering(false);
      return;
    }

    // Poll until we see fresh results (capped at 90s as a safety net)
    const deadline = Date.now() + 90000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2000));
      const data = await fetchHealthChecks();
      if (data && data.length > 0) {
        const latestTs = new Date(data[0].timestamp).getTime();
        if (latestTs > beforeTs) break;
      }
    }
    setTriggering(false);
  };

  const getStatusIcon = (isAccessible) => {
    return isAccessible ? (
      <CheckCircle className="h-5 w-5 text-success-500" />
    ) : (
      <XCircle className="h-5 w-5 text-error-500" />
    );
  };

  const getStatusColor = (isAccessible) => {
    return isAccessible 
      ? 'text-success-600 bg-success-50'
      : 'text-error-600 bg-error-50';
  };

  const getResponseTimeColor = (responseTime) => {
    if (responseTime < 1000) return 'text-success-600';
    if (responseTime < 3000) return 'text-warning-600';
    return 'text-error-600';
  };

  const groupedChecks = healthChecks.reduce((acc, check) => {
    if (!acc[check.url]) {
      acc[check.url] = [];
    }
    acc[check.url].push(check);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-8">
        <AlertCircle className="h-12 w-12 mx-auto mb-4" />
        <p>Failed to load health checks: {error}</p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Health Checks</h1>
          <p className="mt-2 text-sm text-gray-700">
            Monitor the availability and response times of key pages on bajaao.com
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={triggerHealthCheck}
            disabled={triggering}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${triggering ? 'animate-spin' : ''}`} />
            {triggering ? 'Running...' : 'Run Health Check'}
          </button>
        </div>
      </div>

      <div className="mt-8 space-y-6">
        {Object.entries(groupedChecks)
          .sort(([a], [b]) => {
            const aDash = a.includes('test-dashboard.bajaao.org');
            const bDash = b.includes('test-dashboard.bajaao.org');
            if (aDash && !bDash) return 1;
            if (!aDash && bDash) return -1;
            return 0;
          })
          .map(([url, checks]) => {
          const latestCheck = checks[0]; // Assuming checks are ordered by timestamp desc
          const recentChecks = checks.slice(0, 10); // Last 10 checks

          return (
            <div key={url} className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {getStatusIcon(latestCheck.is_accessible)}
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        {url}
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-gray-400 hover:text-gray-600"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </h3>
                      <p className="text-sm text-gray-500">
                        Last checked: {format(new Date(latestCheck.timestamp), 'MMM dd, yyyy HH:mm:ss')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(latestCheck.is_accessible)}`}>
                      {latestCheck.is_accessible ? 'Online' : 'Offline'}
                    </span>
                    <p className="text-sm text-gray-500 mt-1">
                      Status: {latestCheck.status_code}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status Code</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <span className={`font-medium ${latestCheck.status_code >= 200 && latestCheck.status_code < 300 ? 'text-success-600' : 'text-error-600'}`}>
                        {latestCheck.status_code || 'N/A'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Response Time</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <span className={`font-medium ${getResponseTimeColor(latestCheck.response_time)}`}>
                        {latestCheck.response_time.toFixed(0)}ms
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Availability</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <span className="font-medium">
                        {((recentChecks.filter(c => c.is_accessible).length / recentChecks.length) * 100).toFixed(1)}%
                      </span>
                      <span className="text-gray-500 ml-1">
                        (last {recentChecks.length} checks)
                      </span>
                    </dd>
                  </div>
                </div>

                {latestCheck.error_message && (
                  latestCheck.is_accessible ? (
                    // Site is up (2xx/3xx) but the check recorded a note — e.g. it
                    // recovered after a transient retry. That's informational, not a
                    // failure, so surface it as an amber "Recovered" note, never red.
                    <div className="mt-4 p-3 bg-warning-50 border border-warning-200 rounded-md">
                      <h4 className="text-sm font-medium text-warning-800">Recovered</h4>
                      <p className="mt-1 text-sm text-warning-700">{latestCheck.error_message}</p>
                    </div>
                  ) : (
                    <div className="mt-4 p-3 bg-error-50 border border-error-200 rounded-md">
                      <h4 className="text-sm font-medium text-error-800">Error</h4>
                      <p className="mt-1 text-sm text-error-700">{latestCheck.error_message}</p>
                      <FailureDiagnostics check={latestCheck} />
                    </div>
                  )
                )}
              </div>

              {/* Recent checks timeline */}
              <div className="px-6 py-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Recent History</h4>
                <div className="space-y-2">
                  {recentChecks.slice(0, 5).map((check, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <div className="flex items-center">
                        {getStatusIcon(check.is_accessible)}
                        <span className="ml-2 text-gray-900">
                          {format(new Date(check.timestamp), 'MMM dd, HH:mm')}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`${check.status_code >= 200 && check.status_code < 300 ? 'text-success-600' : 'text-error-600'}`}>
                          {check.status_code}
                        </span>
                        <span className={getResponseTimeColor(check.response_time)}>
                          {check.response_time.toFixed(0)}ms
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}

        {Object.keys(groupedChecks).length === 0 && (
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No health checks found</h3>
            <p className="text-gray-500">Run your first health check to see results here.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthChecks;